import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { announcementService, type NotificationSetting } from '../../services/announcement.service';
import {
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS,
  type AnnouncementCategory,
} from '../../constants/announcementCategories';

/** プロフィール内「お知らせメール」: カテゴリごとの受信ON/OFF */
export default function NotificationSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<AnnouncementCategory, boolean> | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['announcements', 'me', 'settings'],
    queryFn: () => announcementService.getMySettings(),
    staleTime: 0,
  });

  useEffect(() => {
    if (!query.data) return;
    const next = {} as Record<AnnouncementCategory, boolean>;
    for (const key of ANNOUNCEMENT_CATEGORIES) next[key] = true;
    for (const row of query.data) next[row.category] = row.emailEnabled;
    setDraft(next);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (settings: NotificationSetting[]) => announcementService.updateMySettings(settings),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['announcements', 'me', 'settings'] });
    },
  });

  const categoryLabel = (key: AnnouncementCategory) =>
    t(`announcements.category_${key}`, { defaultValue: ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS[key] });

  const allOn = draft ? ANNOUNCEMENT_CATEGORIES.every((key) => draft[key]) : false;
  const setAll = (value: boolean) => {
    if (!draft) return;
    const next = { ...draft };
    for (const key of ANNOUNCEMENT_CATEGORIES) next[key] = value;
    setDraft(next);
    setSaved(false);
  };

  return (
    <Card className="mt-3">
      <Card.Header>
        <Card.Title className="mb-0">
          <i className="fas fa-bell me-2"></i>
          {t('announcements.settings_title', { defaultValue: 'お知らせメール' })}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="text-muted small">
          {t('announcements.settings_lead', {
            defaultValue:
              '研助からのお知らせ（EAの更新、各サービスの変更など）を、登録したメールアドレスで受け取るかを種類ごとに選べます。公開されたお知らせはメールの設定にかかわらず「お知らせ」ページで読めます。',
          })}{' '}
          <Link to="/announcements">{t('announcements.settings_open_list', { defaultValue: 'お知らせ一覧' })}</Link>
        </p>
        {query.isLoading || !draft ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate(
                ANNOUNCEMENT_CATEGORIES.map((category) => ({ category, emailEnabled: draft[category] }))
              );
            }}
          >
            <Form.Check
              type="checkbox"
              id="notification-all"
              className="mb-3"
              label={t('announcements.settings_all', { defaultValue: 'すべてのお知らせをメールで受け取る' })}
              checked={allOn}
              onChange={(e) => setAll(e.target.checked)}
            />
            {ANNOUNCEMENT_CATEGORIES.map((key) => (
              <Form.Check
                key={key}
                type="checkbox"
                id={`notification-${key}`}
                className="mb-2 ms-3"
                label={categoryLabel(key)}
                checked={draft[key]}
                onChange={(e) => {
                  setDraft({ ...draft, [key]: e.target.checked });
                  setSaved(false);
                }}
              />
            ))}
            {mutation.isError ? (
              <Alert variant="danger" className="mt-3 small">
                {t('announcements.settings_save_error', { defaultValue: '保存に失敗しました。' })}
              </Alert>
            ) : null}
            {saved ? (
              <Alert variant="success" className="mt-3 small">
                {t('announcements.settings_saved', { defaultValue: '受信設定を保存しました。' })}
              </Alert>
            ) : null}
            <Button type="submit" variant="primary" className="mt-2" disabled={mutation.isPending}>
              {mutation.isPending
                ? t('common.saving', { defaultValue: '保存中...' })
                : t('announcements.settings_save', { defaultValue: '受信設定を保存' })}
            </Button>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
}
